import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Rocket, RefreshCw, Link2, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { DiffGroup } from './DiffGroup';
import { ReleasesList } from './ReleasesList';
import { ReleaseStatusBar } from './ReleaseStatusBar';

const errMsg = (e) => e?.response?.data?.detail || 'حدث خطأ';

const GROUPS = [
  { key: 'new', title: 'جديد سيُضاف', tone: 'emerald' },
  { key: 'updated', title: 'موجود سيُحدَّث', tone: 'blue' },
  { key: 'deleted', title: 'محذوفات تنتظر التأكيد', tone: 'rose' },
  { key: 'conflicts', title: 'تعارضات', tone: 'amber' },
];

export default function PublishManager() {
  const { toast } = useToast();
  const [diff, setDiff] = useState(null);
  const [releases, setReleases] = useState([]);
  const [warnings, setWarnings] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([api.get('/publish/diff'), api.get('/publish/releases')]);
      setDiff(d.data); setReleases(r.data);
    } catch (e) { toast({ title: 'تعذّر التحميل', description: errMsg(e) }); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const decide = async (key, action) => {
    try { await api.post('/publish/decision', { key, action }); } catch (e) { toast({ title: 'فشل', description: errMsg(e) }); }
    load();
  };

  const run = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast({ title: 'فشل', description: errMsg(e) }); }
    setBusy('');
  };

  const checkLinks = () => run('links', async () => {
    const { data } = await api.post('/publish/check-links');
    setWarnings(data.warnings);
  });

  const approve = () => window.confirm('اعتماد هذا الإصدار؟ سيُطبَّق على الموقع الحي عند الضغط على Deploy.') && run('approve', async () => {
    const { data } = await api.post('/publish/approve');
    toast({ title: 'تم اعتماد الإصدار', description: 'اضغط Deploy ليُطبَّق على الموقع الحي.' });
    setWarnings(data.warnings);
    load();
  });

  const releaseAction = (id, action) => run(action + id, async () => {
    await api.post(`/publish/releases/${id}/${action}`);
    toast({ title: action === 'retry' ? 'تمت إعادة المحاولة' : 'تم الاسترجاع' });
    load();
  });

  if (!diff) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  const total = GROUPS.reduce((n, g) => n + diff[g.key].length, 0);
  const pendingConflicts = diff.conflicts.filter(c => !c.decision).length;

  return (
    <div dir="rtl" className="space-y-5 text-right" data-testid="publish-manager">
      <ReleaseStatusBar release={releases[0]} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {GROUPS.map(g => (
          <div key={g.key} data-testid={`publish-count-${g.key}`} className="rounded-xl bg-white border border-slate-200 p-4">
            <div className="text-2xl font-extrabold text-slate-900">{diff[g.key].length}</div>
            <div className="text-xs text-slate-500">{g.title}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="publish-refresh-btn" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 ms-2" />تحديث</Button>
        <Button data-testid="publish-check-links-btn" variant="outline" disabled={!!busy} onClick={checkLinks}>
          {busy === 'links' ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Link2 className="w-4 h-4 ms-2" />}فحص الروابط والملفات
        </Button>
        <Button data-testid="publish-approve-btn" disabled={!!busy || total === 0} onClick={approve} className="bg-blue-600 hover:bg-blue-700 text-white">
          {busy === 'approve' ? <Loader2 className="w-4 h-4 ms-2 animate-spin" /> : <Rocket className="w-4 h-4 ms-2" />}اعتماد الإصدار
        </Button>
      </div>
      {pendingConflicts > 0 && (
        <p data-testid="publish-pending-conflicts-note" className="text-sm text-amber-700">{pendingConflicts} تعارض بدون قرار لن يدخل الإصدار حتى تختار نسخته.</p>
      )}
      <p className="text-xs text-slate-500">الترجمة التلقائية تكمل الحقول الإنجليزية بعد الحفظ بثوانٍ، فاضغط «تحديث» قبل الاعتماد.</p>

      {warnings && <LinkWarnings warnings={warnings} />}

      {total === 0 && <div data-testid="publish-no-changes" className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600 text-sm">لا توجد فروقات بين المعاينة والموقع الحي.</div>}
      {GROUPS.map(g => diff[g.key].length > 0 && (
        <DiffGroup key={g.key} group={g} items={diff[g.key]} onDecide={decide} />
      ))}

      <ReleasesList releases={releases} busy={busy} onAction={releaseAction} />
    </div>
  );
}

const LinkWarnings = ({ warnings }) => (
  <div data-testid="publish-link-warnings" className={`rounded-xl border p-4 text-sm ${warnings.length ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`}>
    {warnings.length === 0 ? 'كل الروابط والملفات سليمة.' : (
      <ul className="space-y-1">
        {warnings.map((w, i) => (
          <li key={i} className="flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span><b>{w.title}</b>: {w.problem} <span dir="ltr" className="break-all text-xs opacity-70">{w.url}</span></span></li>
        ))}
      </ul>
    )}
  </div>
);
